import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { useEspecificacao } from '../contexts/EspecificacaoContext';
import Folha from '../components/especificacao/Folha';
import Quantidade from '../components/especificacao/Quantidade';
import SeletorAmbientes from '../components/especificacao/SeletorAmbientes';
import { FotoItem } from '../components/especificacao/CestaEspecificacao';
import EnvioPdf from '../components/especificacao/EnvioPdf';
import { agruparPorDepartamento, reduzirImagem, dataCurta, situacaoDe, fmtTamanho } from '../utils/especificacao';
import '../components/especificacao/especificacao.css';
import './Especificacoes.css';

/**
 * Especificação montada pelo CATÁLOGO, em 3 etapas: (1) itens por departamento —
 * quantidade, ambientes e observação editáveis; (2) cliente e projeto; (3)
 * documento — logo do escritório e o PDF quantitativo. Abrir aqui torna ela a
 * ativa no catálogo.
 */
function DetalheCatalogo() {
  const { id } = useParams();
  const idNum = Number(id);
  const navigate = useNavigate();
  const { ativa, escolher, carregandoAtiva, atualizarItem, removerItem, abrirLivre, atualizarEsp } = useEspecificacao();
  const [naoAchou, setNaoAchou] = useState(false);
  const [editNome, setEditNome] = useState(null);
  const [editItem, setEditItem] = useState(null); // { item, ambientes, observacao }
  const [logo, setLogo] = useState(undefined); // undefined = carregando; null = sem logo
  const [logoErro, setLogoErro] = useState('');
  const arquivoLogo = useRef(null);
  const esp = ativa?.id === idNum ? ativa : null;

  useEffect(() => {
    if (ativa?.id === idNum) return;
    escolher(idNum).then(d => { if (!d) setNaoAchou(true); });
  }, [idNum]);
  useEffect(() => {
    api.get('/especificacoes/logo').then(r => setLogo(r.data.logo)).catch(() => setLogo(null));
  }, []);

  if (naoAchou) {
    return (
      <div className="esp-vazio py-5">
        <i className="bi bi-question-circle" />
        <strong>Especificação não encontrada</strong>
        <Link to="/especificacoes" className="esp-btn-sec mt-2 text-decoration-none">Ver minhas especificações</Link>
      </div>
    );
  }
  if (!esp) return <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>;

  const grupos = agruparPorDepartamento(esp.itens);
  const total = esp.itens.length;
  const s = situacaoDe(esp);

  const salvarNome = async () => {
    const n = (editNome || '').trim();
    if (n.length >= 2 && n !== esp.nome) await atualizarEsp({ nome: n });
    setEditNome(null);
  };
  const salvarItem = () => {
    atualizarItem(editItem.item.id, { ambientes: editItem.ambientes, observacao: editItem.observacao });
    setEditItem(null);
  };
  const trocarLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLogoErro('');
    try {
      const img = await reduzirImagem(file, { max: 700, qualidade: 0.9 });
      await api.put('/especificacoes/logo', { imagem: img });
      setLogo(img);
    } catch (err) { setLogoErro(err.response?.data?.error || err.message || 'Não foi possível enviar a logo'); }
  };
  const tirarLogo = async () => { await api.delete('/especificacoes/logo'); setLogo(null); };

  return (
    <div className="espd">
      <Link to="/especificacoes" className="espd-voltar"><i className="bi bi-chevron-left" />Minhas especificações</Link>

      <div className="espd-topo">
        <div className="espd-titulo">
          {editNome !== null ? (
            <input className="espd-nome-input" autoFocus value={editNome} maxLength={120}
              onChange={e => setEditNome(e.target.value)} onBlur={salvarNome}
              onKeyDown={e => { if (e.key === 'Enter') salvarNome(); if (e.key === 'Escape') setEditNome(null); }} />
          ) : (
            <h1 onClick={() => setEditNome(esp.nome)} title="Clique pra renomear">{esp.nome}<i className="bi bi-pencil" /></h1>
          )}
          <div className="espd-meta">
            <span className={`espl-sit ${s.classe}`}><i className={`bi ${s.icone}`} />{s.rotulo}{esp.status === 'finalizada' && esp.finalizado_em ? ` em ${dataCurta(esp.finalizado_em)}` : ''}</span>
            <span>{total} {total === 1 ? 'item' : 'itens'} · {grupos.length} {grupos.length === 1 ? 'categoria' : 'categorias'}</span>
          </div>
        </div>
        <div className="espd-acoes">
          <button type="button" className="esp-btn-sec" onClick={() => navigate('/catalogo')}><i className="bi bi-grid-1x2" />Continuar no catálogo</button>
          <button type="button" className="esp-btn" disabled={!total} onClick={() => navigate(`/especificacoes/${esp.id}/pdf`)}>
            <i className="bi bi-file-earmark-pdf" />Gerar PDF
          </button>
        </div>
      </div>

      {/* Etapa 1 — Itens */}
      <section className="espd-etapa">
        <div className="espd-etapa-cab">
          <div className="espd-etapa-tit"><span>1</span>Itens do projeto</div>
          <div className="d-flex gap-2 flex-wrap">
            <button type="button" className="esp-btn-sec btn-sm-esp" onClick={() => navigate('/catalogo')}><i className="bi bi-plus-lg" />Do catálogo</button>
            <button type="button" className="esp-btn-sec btn-sm-esp" onClick={() => abrirLivre()}><i className="bi bi-lightbulb" />Fora do catálogo</button>
          </div>
        </div>

        {carregandoAtiva && !total && <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>}
        {!total && !carregandoAtiva && (
          <div className="esp-vazio">
            <i className="bi bi-bag-plus" />
            <strong>Nenhum item ainda</strong>
            <span>Vá ao catálogo e toque em <b>+ Especificar</b> — os itens entram aqui, agrupados por categoria.</span>
          </div>
        )}

        {grupos.map(g => (
          <div key={g.nome} className="espd-grupo">
            <div className="espd-grupo-tit">{g.nome}<span>{g.itens.length}</span></div>
            <div className="espd-tab">
              {g.itens.map(i => (
                <div key={i.id} className="espd-linha">
                  <FotoItem item={i} className="espd-foto" />
                  <div className="espd-prod">
                    {i.livre
                      ? <div className="espd-prod-nome">{i.nome}</div>
                      : <Link to={`/catalogo/${i.codpro}`} className="espd-prod-nome">{i.nome}</Link>}
                    <div className="espd-prod-meta">
                      {[i.marca, i.medida, i.codpro ? `Cód ${i.codpro}` : null].filter(Boolean).join(' · ')}
                      {i.livre && <span className="esp-tag-livre">fora do catálogo</span>}
                      {!i.livre && !i.disponibilidade && <span className="esp-tag-livre alerta">saiu de linha</span>}
                      {!i.livre && i.pagaComissao && <span className="espd-tag-com" title="Paga comissão por indicação"><i className="bi bi-cash-coin" /></span>}
                    </div>
                    {i.observacao && <div className="espd-obs"><i className="bi bi-chat-left-text" />{i.observacao}</div>}
                  </div>
                  <button type="button" className="espd-amb" onClick={() => setEditItem({ item: i, ambientes: i.ambientes, observacao: i.observacao || '' })}>
                    {i.ambientes.length ? <><span className="rot">Para:</span> {i.ambientes.join(', ')}</> : <span className="vazio">+ Ambientes</span>}
                    <i className="bi bi-pencil" />
                  </button>
                  <div className="espd-qtd">
                    <Quantidade compacto valor={i.quantidade} unidade={i.unidade} onChange={q => atualizarItem(i.id, { quantidade: q })} />
                  </div>
                  <div className="espd-lin-acoes">
                    {i.livre && <button type="button" className="esp-icone" onClick={() => abrirLivre(i)} aria-label="Editar item"><i className="bi bi-pencil-square" /></button>}
                    <button type="button" className="esp-icone perigo" onClick={() => removerItem(i.id)} aria-label="Remover item"><i className="bi bi-trash3" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* Etapa 2 — Cliente e projeto */}
      <ClienteProjeto key={esp.id} numero={2} esp={esp} salvar={atualizarEsp} />

      {/* Etapa 3 — Documento */}
      <section className="espd-etapa">
        <div className="espd-etapa-cab"><div className="espd-etapa-tit"><span>3</span>Documento pro cliente</div></div>
        <div className="espd-doc">
          <div className="espd-logo">
            <div className="espd-logo-prev">
              {logo === undefined ? <div className="spinner-border spinner-border-sm" />
                : logo ? <img src={logo} alt="Logo do escritório" />
                  : <span><i className="bi bi-image" />Sua logo</span>}
            </div>
            <div>
              <strong>Logo do seu escritório</strong>
              <p>Vai no topo do PDF, ao lado da logo da Centrão. Fica salva pras próximas especificações.</p>
              <div className="d-flex gap-2 flex-wrap">
                <button type="button" className="esp-btn-sec btn-sm-esp" onClick={() => arquivoLogo.current?.click()}>
                  <i className="bi bi-upload" />{logo ? 'Trocar logo' : 'Enviar logo'}
                </button>
                {logo && <button type="button" className="esp-link" onClick={tirarLogo}>Remover</button>}
              </div>
              {logoErro && <div className="esp-erro mt-2"><i className="bi bi-exclamation-circle" />{logoErro}</div>}
              <input ref={arquivoLogo} type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={trocarLogo} />
            </div>
          </div>
          <div className="espd-pdf">
            <i className="bi bi-file-earmark-pdf" />
            <div>
              <strong>Quantitativo em PDF</strong>
              <p>Organizado por categoria, com foto, quantidade e ambiente de cada item. Sem preços; quantidades líquidas (sem margem de perda).</p>
              <button type="button" className="esp-btn" disabled={!total} onClick={() => navigate(`/especificacoes/${esp.id}/pdf`)}>
                <i className="bi bi-file-earmark-pdf" />Gerar PDF
              </button>
            </div>
          </div>
        </div>
      </section>

      {editItem && (
        <Folha
          titulo="Ambientes e observação"
          subtitulo={editItem.item.nome}
          onFechar={() => setEditItem(null)}
          rodape={(
            <>
              <button type="button" className="esp-btn-sec" onClick={() => setEditItem(null)}>Cancelar</button>
              <button type="button" className="esp-btn" onClick={salvarItem}>Salvar</button>
            </>
          )}
        >
          <label className="esp-rotulo mt-0">Para quais ambientes?</label>
          <SeletorAmbientes valor={editItem.ambientes} onChange={a => setEditItem({ ...editItem, ambientes: a })} sugestoes={esp.sugestoesAmbiente} />
          <label className="esp-rotulo">Observação</label>
          <textarea className="esp-campo" rows={2} maxLength={500} value={editItem.observacao}
            placeholder="Ex.: rejunte cinza platina, paginação 1/3"
            onChange={e => setEditItem({ ...editItem, observacao: e.target.value })} />
        </Folha>
      )}
    </div>
  );
}

/** Cliente e projeto (nome do cliente, contato e observações) — salva ao sair do campo. */
function ClienteProjeto({ numero, esp, salvar, notaObs = 'vão no PDF' }) {
  const { avisar } = useEspecificacao();
  const [v, setV] = useState({ cliente_nome: esp.cliente_nome || '', cliente_contato: esp.cliente_contato || '', observacao: esp.observacao || '' });
  const [salvo, setSalvo] = useState('');
  const salvarCampo = async (campo) => {
    if ((esp[campo] || '') === v[campo]) return;
    try { await salvar({ [campo]: v[campo] }); setSalvo(campo); setTimeout(() => setSalvo(''), 1800); }
    catch (e) { avisar(e.response?.data?.error || 'Não foi possível salvar'); }
  };
  const ok = (campo) => salvo === campo && <span className="espd-salvo"><i className="bi bi-check2" />salvo</span>;
  return (
    <section className="espd-etapa">
      <div className="espd-etapa-cab"><div className="espd-etapa-tit"><span>{numero}</span>Cliente e projeto</div></div>
      <div className="esp-grade2">
        <div>
          <label className="esp-rotulo">Nome do cliente {ok('cliente_nome')}</label>
          <input className="esp-campo" maxLength={120} value={v.cliente_nome} placeholder="Nome do cliente"
            onChange={e => setV({ ...v, cliente_nome: e.target.value })} onBlur={() => salvarCampo('cliente_nome')} />
        </div>
        <div>
          <label className="esp-rotulo">Contato <span className="opc">opcional</span> {ok('cliente_contato')}</label>
          <input className="esp-campo" maxLength={120} value={v.cliente_contato} placeholder="Telefone ou e-mail"
            onChange={e => setV({ ...v, cliente_contato: e.target.value })} onBlur={() => salvarCampo('cliente_contato')} />
        </div>
      </div>
      <label className="esp-rotulo">Observações gerais <span className="opc">{notaObs}</span> {ok('observacao')}</label>
      <textarea className="esp-campo" rows={3} maxLength={1000} value={v.observacao}
        placeholder="Ex.: Obra prevista pra março. Revestimentos da área externa ainda em definição."
        onChange={e => setV({ ...v, observacao: e.target.value })} onBlur={() => salvarCampo('observacao')} />
    </section>
  );
}

/**
 * Especificação com o PDF PRÓPRIO do arquiteto (feito no programa dele): ver o
 * arquivo, baixar, trocar, e os dados do cliente/projeto. Não tem itens nem vira
 * a especificação ativa do catálogo.
 */
function DetalhePdf({ id }) {
  const { recarregar, avisar } = useEspecificacao();
  const [esp, setEsp] = useState(null);
  const [naoAchou, setNaoAchou] = useState(false);
  const [url, setUrl] = useState(null);          // PDF em blob (baixado com o token)
  const [envio, setEnvio] = useState(null);      // arquivo sendo enviado
  const [progresso, setProgresso] = useState(null);
  const [trocando, setTrocando] = useState(false);
  const [editNome, setEditNome] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get(`/especificacoes/${id}`);
      setEsp(r.data);
      if (r.data.pdf) {
        const b = await api.get(`/especificacoes/${id}/pdf`, { responseType: 'blob' });
        setUrl(URL.createObjectURL(new Blob([b.data], { type: 'application/pdf' })));
      } else setUrl(null);
    } catch { setNaoAchou(true); }
  }, [id]);
  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]); // libera o blob anterior

  const salvar = async (dados) => {
    await api.put(`/especificacoes/${id}`, dados);
    setEsp(e => ({ ...e, ...dados }));
    recarregar();
  };
  const salvarNome = async () => {
    const n = (editNome || '').trim();
    if (n.length >= 2 && n !== esp.nome) await salvar({ nome: n });
    setEditNome(null);
  };
  const enviar = async (file) => {
    setEnvio(file); setProgresso(0);
    const fd = new FormData();
    fd.append('arquivo', file);
    try {
      await api.put(`/especificacoes/${id}/pdf`, fd, {
        onUploadProgress: e => setProgresso(Math.round((e.loaded * 100) / (e.total || file.size))),
      });
      avisar(esp.pdf ? 'PDF trocado' : 'PDF enviado');
      setTrocando(false);
      await carregar();
      recarregar();
    } catch (e) { avisar(e.response?.data?.error || 'Não foi possível enviar o PDF'); }
    finally { setEnvio(null); setProgresso(null); }
  };
  const baixar = () => {
    const a = document.createElement('a');
    a.href = url;
    a.download = esp.pdf?.nome || `${esp.nome}.pdf`;
    a.click();
  };

  if (naoAchou) {
    return (
      <div className="esp-vazio py-5">
        <i className="bi bi-question-circle" />
        <strong>Especificação não encontrada</strong>
        <Link to="/especificacoes" className="esp-btn-sec mt-2 text-decoration-none">Ver minhas especificações</Link>
      </div>
    );
  }
  if (!esp) return <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>;
  const s = situacaoDe(esp);

  return (
    <div className="espd">
      <Link to="/especificacoes" className="espd-voltar"><i className="bi bi-chevron-left" />Minhas especificações</Link>

      <div className="espd-topo">
        <div className="espd-titulo">
          {editNome !== null ? (
            <input className="espd-nome-input" autoFocus value={editNome} maxLength={120}
              onChange={e => setEditNome(e.target.value)} onBlur={salvarNome}
              onKeyDown={e => { if (e.key === 'Enter') salvarNome(); if (e.key === 'Escape') setEditNome(null); }} />
          ) : (
            <h1 onClick={() => setEditNome(esp.nome)} title="Clique pra renomear">{esp.nome}<i className="bi bi-pencil" /></h1>
          )}
          <div className="espd-meta">
            <span className={`espl-sit ${s.classe}`}><i className={`bi ${s.icone}`} />{s.rotulo}</span>
            <span><i className="bi bi-file-earmark-pdf-fill" style={{ color: '#c0392b' }} /> PDF próprio</span>
          </div>
        </div>
        {url && (
          <div className="espd-acoes">
            <button type="button" className="esp-btn-sec" onClick={() => window.open(url, '_blank')}><i className="bi bi-box-arrow-up-right" />Abrir</button>
            <button type="button" className="esp-btn" onClick={baixar}><i className="bi bi-download" />Baixar PDF</button>
          </div>
        )}
      </div>

      {/* Etapa 1 — o PDF */}
      <section className="espd-etapa">
        <div className="espd-etapa-cab">
          <div className="espd-etapa-tit"><span>1</span>PDF do projeto</div>
          {esp.pdf && !trocando && (
            <button type="button" className="esp-btn-sec btn-sm-esp" onClick={() => setTrocando(true)}><i className="bi bi-arrow-repeat" />Trocar PDF</button>
          )}
        </div>

        {(!esp.pdf || trocando) && (
          <div className="mt-2">
            <EnvioPdf arquivo={envio} progresso={progresso} desabilitado={progresso !== null} onEscolher={enviar} />
            {trocando && progresso === null && (
              <button type="button" className="esp-link mt-2" onClick={() => setTrocando(false)}>Cancelar troca</button>
            )}
          </div>
        )}

        {esp.pdf && !trocando && (
          <>
            <div className="espd-arq">
              <i className="bi bi-file-earmark-pdf-fill" />
              <div>
                <strong>{esp.pdf.nome}</strong>
                <span>{fmtTamanho(esp.pdf.tamanho)} · enviado em {dataCurta(esp.pdf.enviado_em)}</span>
              </div>
              {url && <button type="button" className="esp-btn-sec btn-sm-esp espd-arq-abrir" onClick={() => window.open(url, '_blank')}>Abrir PDF</button>}
            </div>
            {url
              ? <iframe className="espd-pdf-ver" src={url} title={`PDF — ${esp.nome}`} />
              : <div className="text-center py-4"><div className="spinner-border spinner-border-sm" /></div>}
          </>
        )}
      </section>

      <ClienteProjeto key={esp.id} numero={2} esp={esp} salvar={salvar} notaObs="opcional" />
    </div>
  );
}

/**
 * Detalhe da especificação: montada pelo catálogo (itens + PDF gerado) ou com o
 * PDF próprio do arquiteto. A origem vem da lista (já carregada) ou de uma consulta.
 */
export default function EspecificacaoDetalhe() {
  const { id } = useParams();
  const idNum = Number(id);
  const { lista } = useEspecificacao();
  const daLista = lista.find(e => e.id === idNum)?.origem;
  const [origem, setOrigem] = useState(null);
  useEffect(() => {
    if (daLista) return;
    api.get(`/especificacoes/${idNum}`)
      .then(r => setOrigem(r.data.origem || 'catalogo'))
      .catch(() => setOrigem('catalogo')); // o detalhe do catálogo mostra "não encontrada"
  }, [idNum, daLista]);
  const o = daLista || origem;
  if (!o) return <div className="text-center py-5"><div className="spinner-border" style={{ color: 'var(--primary)' }} /></div>;
  return o === 'pdf' ? <DetalhePdf key={idNum} id={idNum} /> : <DetalheCatalogo key={idNum} />;
}
